import React, { useState, useEffect } from 'react';
import StripeCancelSubscription from '../../../graphql/mutations/StripeCancelSubscription';
import StripeReSubscribe from '../../../graphql/mutations/StripeReSubscribe';
import { toast } from 'react-toastify';
import {
    Box,
    Typography,
    Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CheckoutButton } from './StripeCheckoutButton';
import Users from '../../../graphql/queries/Users';
import ConfirmableButton from '../../Shared/ConfirmableButton';

const SubscriptionPage = () => {
    const { t } = useTranslation();

    const [subscription, setSubscription] = useState(null);
    const [error, setError] = useState('');
    const [initialLoading, setInitialLoading] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        Users
            .fetch()
            .then((data) => {
                const user = data?.me;
                setSubscription(user?.subscription || null);
            })
            .catch((err) => {
                console.error('Failed to load subscription', err);
                setError('Failed to load subscription');
            })
            .finally(() => setInitialLoading(false));
    }, []);

    const hasSubscription = !!subscription;
    const isCancelled = subscription?.status === 'canceled';
    const isActive = subscription?.status === 'active';

    const handleResubscribe = () => {
        setIsLoading(true);
        StripeReSubscribe.commit({}).then(({ stripeReSubscribe }) => {
            const { success, error } = stripeReSubscribe || {};
            if (success) {
                toast.success(t('profile.tabs.subscription.subscription_resubscribe_successfully'));
                setSubscription(prev => ({ ...prev, cancelAtPeriodEnd: false }));
            } else {
                toast.error(error || 'Failed to update subscription.');
            }
            setIsLoading(false);
        }).catch((err) => {
            console.error('Resubcribe subscription error:', err);
            toast.error('An unexpected error occurred.');
            setIsLoading(false);
        });
    };

    const handleUnsubscribe = () => {
        setIsLoading(true);
        StripeCancelSubscription.commit({}).then(({ stripeCancelSubscription }) => {
            const { success, error } = stripeCancelSubscription || {};
            if (success) {
                toast.success(t('profile.tabs.subscription.subscription_cancelled_message'));
                setSubscription(prev => ({ ...prev, cancelAtPeriodEnd: true }));
            } else {
                toast.error(error || 'Failed to cancel subscription.');
            }
            setIsLoading(false);
        }).catch((err) => {
            console.error('Cancel subscription error:', err);
            toast.error('An unexpected error occurred.');
            setIsLoading(false);
        });
    };


    if (initialLoading) {
        return <Typography variant="h6">{t('common.loading', 'Loading...')}</Typography>;
    }

    console.log(subscription)

    return (
        <Box sx={{ p: 2 }}>
            {/*<Alert severity="error" sx={{ mb: 2 }}>{'error'}</Alert>*/}
            <Typography variant="h4" gutterBottom>
                {t('profile.tabs.subscription.subtitle')}
            </Typography>
            <Box sx={{ mb: 4, bgcolor: 'background.paper', p: 2, borderRadius: 2, boxShadow: 1 }}>
                <Typography variant="body1" gutterBottom>
                    {!hasSubscription ? (
                      <Box>
                          <Alert severity="info" sx={{ mb: 2 }}>
                              {t('profile.tabs.subscription.no_subscription_access_message')}
                          </Alert>
                          <CheckoutButton />
                      </Box>
                    ) : (
                      <Box>
                          <Typography variant="h6" gutterBottom>
                              {subscription.planName}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                  {t('profile.tabs.subscription.status')}:
                              </Typography>
                              <Typography
                                  variant="body2"
                                  sx={{
                                      px: 1.5,
                                      py: 0.5,
                                      borderRadius: 1,
                                      bgcolor: subscription.status === 'active' ? 'success.light' : 'warning.light',
                                      color: subscription.status === 'active' ? 'success.dark' : 'warning.dark',
                                      fontWeight: 500,
                                      textTransform: 'capitalize'
                                  }}
                              >
                                  {subscription.status === 'error' ? subscription.errorMessage : subscription.status}
                              </Typography>
                          </Box>
                          {subscription.cancelAtPeriodEnd && (
                            <Box>
                                <Alert severity="warning" sx={{ my: 2 }}>
                                    {`This subscription will end at the end of the current period. ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`}
                                </Alert>
                                <ConfirmableButton
                                  onConfirm={handleResubscribe}
                                  buttonProps={{
                                      variant: 'outlined',
                                      color: 'primary',
                                      disabled: isLoading,
                                      children: isLoading ? t('profile.tabs.subscription.cancelling') || 'Скасування...' : t('profile.tabs.subscription.resubscribe_button')
                                  }}
                                  body={t('profile.tabs.subscription.resubscribe_confirmation_body')}
                                  confirmMessage={t('profile.tabs.subscription.resubscribe_confirmation')}
                                />
                            </Box>
                          )}
                          {subscription.currentPeriodEnd && !subscription.cancelAtPeriodEnd && (
                            <Alert severity="warning" sx={{ my: 2 }}>
                                {t('profile.tabs.subscription.subscription_will_renew', {
                                  date: new Date(subscription.currentPeriodEnd).toLocaleDateString()
                                })}
                            </Alert>
                          )}
                          {isActive && !subscription.cancelAtPeriodEnd && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                              <ConfirmableButton
                                onConfirm={handleUnsubscribe}
                                buttonProps={{
                                    variant: 'outlined',
                                    color: 'error',
                                    disabled: isLoading,
                                    children: isLoading ? t('profile.tabs.subscription.cancelling') || 'Cancelling...' : t('profile.tabs.subscription.unsubscribe_button')
                                }}
                                body={t('profile.tabs.subscription.confirmation_body')}
                                confirmMessage={t('profile.tabs.subscription.confirmation')}
                              />
                            </Box>
                          )}
                          {isCancelled && (
                            <Box sx={{ mt: 2 }}>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    {t('profile.tabs.subscription.subscription_cancelled_message')}
                                </Alert>
                                <CheckoutButton />
                            </Box>
                          )}
                      </Box>
                    )}
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}


        </Box>
    );
};

export default SubscriptionPage;
